/**
 * Service pour charger et gérer les aventures - Identique à Flutter
 */

import {Adventure, ListAdventure, ListAdventureFromAdventure} from '../models/Adventure';
import {PlayerAdventure, PlayerAdventureFromJson} from '../models/PlayerAdventure';
import {PlayerStep, PlayerStepFromJson} from '../models/PlayerStep';
import apiService from './apiService';
import databaseService from './databaseService';
import axios from 'axios';
import ConfigService from './configService';
import getSupabaseClient from './supabaseService';
import sessionBridgeService from './sessionBridgeService';

interface AdventureResponse {
  adventures: Adventure[];
}

class AdventureService {
  /**
   * Charge les aventures depuis l'API
   * Format attendu: {adventures: [...]}
   */
  async loadAdventures(): Promise<Adventure[]> {
    try {
      const response = await apiService.get<AdventureResponse>('/api/adventure');
      if (!response || !response.adventures) {
        throw new Error('Invalid response format: missing "adventures" key');
      }
      return response.adventures;
    } catch (error) {
      console.error('[AdventureService] Error loading adventures from API:', error);
      throw error;
    }
  }

  /**
   * Charge une aventure complète depuis l'API (avec content)
   * Utilisé pour télécharger une aventure
   */
  async loadAdventurePublished(
    uniqueURI: string,
    onProgress?: (received: number) => void,
  ): Promise<Adventure | null> {
    try {
      // Utiliser axios directement pour avoir onDownloadProgress
      const supabase = getSupabaseClient();
      const session = await supabase.auth.getSession();
      const headers: any = {
        'Content-Type': 'application/json',
      };
      
      if (session.data.session?.access_token) {
        headers.Authorization = `Bearer ${session.data.session.access_token}`;
      }

      const response = await axios.get(`${ConfigService.apiBaseUrl}/api/adventure/${uniqueURI}`, {
        onDownloadProgress: (progressEvent: any) => {
          if (onProgress && progressEvent.total) {
            onProgress(progressEvent.loaded);
          }
        },
        headers,
      });
      
      const adventure = response.data;
      
      // Extraire l'image base64 du content si disponible
      if (!adventure.image && adventure.content) {
        try {
          const contentJson = JSON.parse(adventure.content);
          adventure.image = contentJson.image as string | undefined;
        } catch (e) {
          console.error('[AdventureService] Failed to parse content JSON:', e);
        }
      }
      
      return adventure;
    } catch (error) {
      console.error('[AdventureService] Error loading adventure published:', error);
      return null;
    }
  }

  /**
   * Charge une PlayerAdventure complète depuis le JSON stocké localement
   * Identique à Flutter AdventureService.loadPlayerAdventure
   */
  async loadPlayerAdventure(uniqueURI: string): Promise<PlayerAdventure> {
    console.log(`[AdventureService] Loading player adventure: ${uniqueURI}`);

    try {
      // Charger l'aventure depuis la base de données
      const adventure = await databaseService.getAdventureByUniqueURI(uniqueURI);
      if (!adventure) {
        throw new Error(`Adventure not found in database: ${uniqueURI}`);
      }

      console.log(`[AdventureService] Adventure loaded from database:`);
      console.log(`[AdventureService] - name: ${adventure.name}`);
      console.log(`[AdventureService] - type: ${adventure.type}`);
      console.log(`[AdventureService] - content length: ${adventure.content?.length ?? 0}`);

      // Parser le contenu de l'aventure
      if (!adventure.content) {
        throw new Error('Adventure content is missing');
      }

      const content = JSON.parse(adventure.content) as any;
      if (!content.steps || !Array.isArray(content.steps)) {
        throw new Error('Invalid adventure content: missing or invalid steps');
      }

      // Créer l'objet PlayerAdventure
      const playerAdventure = PlayerAdventureFromJson({
        uniqueURI: adventure.uniqueURI,
        name: adventure.name,
        type: adventure.type || 'SIMPLE',
        startNumero: content.startNumero || 1,
        steps: content.steps,
        description: adventure.description,
        cardGFontTitle: adventure.cardGFontTitle,
        image: adventure.image || content.image,
        extraFields: content.extraFields,
        objects: content.objects,
        attributes: content.attributes,
        backupAllowed: content.backupAllowed ?? true,
      });

      console.log(`[AdventureService] Adventure parsed successfully:`);
      console.log(`[AdventureService] - startNumber: ${playerAdventure.startNumero}`);
      console.log(`[AdventureService] - steps: ${playerAdventure.steps.length}`);

      return playerAdventure;
    } catch (error) {
      console.error('[AdventureService] Failed to load player adventure:', error);
      throw error;
    }
  }

  /**
   * Charge une PlayerAdventure en mode édition directement depuis l'API backend
   * Portage de Flutter ApiService.loadEditingPlayerAdventure / AdventureServiceEditing.loadPlayerAdventure
   */
  async loadEditingPlayerAdventure(uniqueURI: string): Promise<PlayerAdventure> {
    console.log(`[AdventureService] Loading EDITING player adventure: ${uniqueURI}`);

    try {
      // En mode popup/test, utiliser le token depuis le bridge (parent laventurier-ui)
      // Sinon, utiliser le token Supabase normal
      let accessToken: string | null = null;

      if (sessionBridgeService.isInPopup()) {
        console.log('[AdventureService] In popup context, waiting for bridge token...');
        // Attendre que le token arrive depuis le parent (peut prendre quelques secondes)
        accessToken = await sessionBridgeService.waitForToken(10000);
        if (!accessToken) {
          throw new Error(
            'No access token available from parent after timeout. Make sure laventurier-ui is running and the popup was opened from it.',
          );
        }
        console.log(`[AdventureService] Bridge token available: ${accessToken.substring(0, 20)}...`);
      } else {
        // Mode normal: utiliser Supabase
        const supabase = getSupabaseClient();
        const {
          data: {session},
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('[AdventureService] Error getting session:', sessionError);
          throw new Error(`Authentication error: ${sessionError.message}`);
        }

        if (!session?.access_token) {
          console.error('[AdventureService] No access token in session');
          throw new Error('No access token available. Please log in.');
        }

        accessToken = session.access_token;
        console.log(`[AdventureService] Supabase token available: ${accessToken.substring(0, 20)}...`);
      }

      const headers: any = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      };

      const url = `${ConfigService.apiBaseUrl}/api/editor/player/adventure/${uniqueURI}`;
      console.log(`[AdventureService] Requesting: ${url}`);

      const response = await axios.get(url, {headers});

      const data = response.data;
      console.log(
        '[AdventureService] Editing player adventure response keys:',
        Object.keys(data || {}),
      );

      const playerAdventure = PlayerAdventureFromJson(data);
      console.log(
        `[AdventureService] Editing player adventure loaded successfully (type=${playerAdventure.type}, steps=${playerAdventure.steps.length})`,
      );

      return playerAdventure;
    } catch (error: any) {
      console.error('[AdventureService] Failed to load EDITING player adventure:', error);
      if (error.response) {
        console.error(
          `[AdventureService] Response status: ${error.response.status}`,
        );
        console.error(
          `[AdventureService] Response data:`,
          error.response.data,
        );
        if (error.response.status === 403) {
          throw new Error(
            `Access forbidden. You may not have editor permissions for adventure ${uniqueURI}. Status: ${error.response.status}`,
          );
        }
        if (error.response.status === 404) {
          throw new Error(
            `Adventure not found: ${uniqueURI}. Status: ${error.response.status}`,
          );
        }
      }
      throw error;
    }
  }

  /**
   * Charge un PlayerStep en mode édition directement depuis l'API backend
   * Portage de Flutter ApiService.loadEditingPlayerStep / AdventureServiceEditing.loadPlayerStep
   */
  async loadEditingPlayerStep(uniqueURI: string, stepNumber: number): Promise<PlayerStep> {
    console.log(
      `[AdventureService] Loading EDITING player step: ${uniqueURI}/step/${stepNumber}`,
    );

    try {
      // En mode popup/test, utiliser le token depuis le bridge (parent laventurier-ui)
      // Sinon, utiliser le token Supabase normal
      let accessToken: string | null = null;

      if (sessionBridgeService.isInPopup()) {
        console.log('[AdventureService] In popup context, waiting for bridge token...');
        // Attendre que le token arrive depuis le parent (peut prendre quelques secondes)
        accessToken = await sessionBridgeService.waitForToken(10000);
        if (!accessToken) {
          throw new Error(
            'No access token available from parent after timeout. Make sure laventurier-ui is running and the popup was opened from it.',
          );
        }
        console.log(`[AdventureService] Bridge token available: ${accessToken.substring(0, 20)}...`);
      } else {
        // Mode normal: utiliser Supabase
        const supabase = getSupabaseClient();
        const {
          data: {session},
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('[AdventureService] Error getting session:', sessionError);
          throw new Error(`Authentication error: ${sessionError.message}`);
        }

        if (!session?.access_token) {
          console.error('[AdventureService] No access token in session');
          throw new Error('No access token available. Please log in.');
        }

        accessToken = session.access_token;
        console.log(`[AdventureService] Supabase token available: ${accessToken.substring(0, 20)}...`);
      }

      const headers: any = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      };

      const url = `${ConfigService.apiBaseUrl}/api/editor/player/adventure/${uniqueURI}/step/${stepNumber}`;
      console.log(`[AdventureService] Requesting: ${url}`);

      const response = await axios.get(url, {headers});

      const data = response.data;
      console.log(
        '[AdventureService] Editing player step response keys:',
        Object.keys(data || {}),
      );

      // Parser le PlayerStep depuis le JSON de l'API (comme Flutter PlayerStep.fromJson)
      const step = PlayerStepFromJson(data);
      console.log(
        `[AdventureService] Editing player step loaded: numero=${step.numero}, paths=${step.paths?.length || 0}, actions=${step.actions?.length || 0}`,
      );

      return step;
    } catch (error: any) {
      console.error('[AdventureService] Failed to load EDITING player step:', error);
      if (error.response) {
        console.error(
          `[AdventureService] Response status: ${error.response.status}`,
        );
        console.error(
          `[AdventureService] Response data:`,
          error.response.data,
        );
        if (error.response.status === 403) {
          throw new Error(
            `Access forbidden. You may not have editor permissions for step ${stepNumber} of adventure ${uniqueURI}. Status: ${error.response.status}`,
          );
        }
        if (error.response.status === 404) {
          throw new Error(
            `Step ${stepNumber} not found in adventure ${uniqueURI}. Status: ${error.response.status}`,
          );
        }
      }
      throw error;
    }
  }

  /**
   * Charge un PlayerStep spécifique depuis une aventure
   * Identique à Flutter AdventureService.loadPlayerStep
   * EN MODE ÉDITION : utilise automatiquement loadEditingPlayerStep (API) au lieu de la DB locale
   */
  async loadPlayerStep(uniqueURI: string, stepNumber: number): Promise<PlayerStep> {
    console.log(`[AdventureService] Loading player step: ${uniqueURI}/step/${stepNumber}`);

    try {
      // EN MODE ÉDITION : utiliser loadEditingPlayerStep (API) au lieu de charger depuis la DB locale
      // Détecter le mode édition via window.opener (popup) ou sessionBridgeService
      const isEditingMode = typeof window !== 'undefined' && (window.opener !== null || sessionBridgeService.isInPopup());
      
      if (isEditingMode) {
        console.log(`[AdventureService] loadPlayerStep - Editing mode detected, loading step from API`);
        return await this.loadEditingPlayerStep(uniqueURI, stepNumber);
      }

      // Mode normal : charger l'aventure complète depuis la DB locale
      const adventure = await this.loadPlayerAdventure(uniqueURI);

      // Trouver l'étape demandée
      const step = adventure.steps.find((s) => s.numero === stepNumber);
      if (!step) {
        throw new Error(`Step ${stepNumber} not found in adventure`);
      }

      console.log(`[AdventureService] Step loaded successfully:`);
      console.log(`[AdventureService] - numero: ${step.numero}`);
      console.log(`[AdventureService] - paths: ${step.paths.length}`);
      console.log(`[AdventureService] - actions: ${step.actions.length}`);
      console.log(`[AdventureService] - attributes: ${step.attributes.length}`);
      console.log(`[AdventureService] - notebook: ${step.notebook ? `"${step.notebook.substring(0, 50)}${step.notebook.length > 50 ? '...' : ''}"` : 'null/undefined'}`);

      return step;
    } catch (error) {
      console.error('[AdventureService] Failed to load player step:', error);
      throw error;
    }
  }

  /**
   * Charge les aventures depuis la base de données locale
   */
  async getLocalAdventures(): Promise<Adventure[]> {
    try {
      const adventures = await databaseService.getAdventures();
      return adventures;
    } catch (error) {
      console.error('[AdventureService] Error loading local adventures:', error);
      return [];
    }
  }

  /**
   * Fusionne les aventures de l'API et de la DB
   * Identique à la logique Flutter dans ListScreen
   */
  async mergeAdventures(
    localAdventures: Adventure[],
    apiAdventures: Adventure[],
  ): Promise<ListAdventure[]> {
    // Conversion en DTO
    const dbDtos = localAdventures.map((a) => ListAdventureFromAdventure(a, a.version));
    const apiDtos = apiAdventures.map((a) => ListAdventureFromAdventure(a));

    // Fusion sur DTO (logique identique à Flutter)
    const merged = new Map<string, ListAdventure>();

    // D'abord, ajouter toutes les aventures de la base de données
    for (const db of dbDtos) {
      const api = apiDtos.find((a) => a.uniqueURI === db.uniqueURI);
      // On garde la version locale mais on met à jour les métadonnées
      if (api) {
        merged.set(db.uniqueURI, {...api, localVersion: db.version});
      } else {
        merged.set(db.uniqueURI, db);
      }
    }

    // Ensuite, ajouter les nouvelles aventures de l'API
    for (const api of apiDtos) {
      if (!merged.has(api.uniqueURI)) {
        merged.set(api.uniqueURI, api);
      }
    }

    // Trier les aventures : d'abord celles qui sont téléchargées, puis les autres
    const sorted = Array.from(merged.values()).sort((a, b) => {
      if (a.localVersion != null && b.localVersion == null) return -1;
      if (a.localVersion == null && b.localVersion != null) return 1;
      return a.name.localeCompare(b.name);
    });

    return sorted;
  }

  /**
   * Sauvegarde une aventure dans la base de données locale
   */
  async saveAdventure(adventure: Adventure): Promise<void> {
    await databaseService.saveAdventure(adventure);
  }

  /**
   * Télécharge et sauvegarde une aventure
   */
  async downloadOrUpdateAdventure(
    uniqueURI: string,
    onProgress?: (progress: number) => void,
  ): Promise<void> {
    const adventure = await this.loadAdventurePublished(uniqueURI, onProgress);
    if (!adventure) {
      throw new Error(`Aucune donnée reçue pour ${uniqueURI}`);
    }
    
    await this.saveAdventure(adventure);
  }

  /**
   * Vérifie si une aventure est disponible localement
   */
  async isAdventureLocal(uniqueURI: string): Promise<boolean> {
    const localAdventures = await this.getLocalAdventures();
    return localAdventures.some((a) => a.uniqueURI === uniqueURI);
  }
}

export default new AdventureService();
